import type { TeacherType } from '@mathflat/domain/Teacher'
import { makeAutoObservable } from 'mobx'
import type { Academy } from 'src/dto'

import type { CustomSignatureFeatureType } from '@/types/learningContent'

import type { PermissionChecker } from '.'
import { FeaturePermission, type TeacherFeatureKey } from './FeaturePermission'
import { RoutePermission } from './RoutePermission'
import type { RoutePermissionType } from './RoutePermission.type'
import { SubscriptionPermission } from './SubscriptionPermission'

/**
 * 권한 트리(브랜드·롤 축)가 해당 feature 를 여는지 판정하는 매핑.
 *
 * 학원 단위 restriction 과는 다른 축이라, 여기 등록된 key 만 라우트 게이팅에서 두 축을 함께 본다.
 * 등록하지 않은 key 는 restriction 축만 보므로 메뉴는 트리로 숨어도 URL 직접 진입은 열려 있다.
 * 나머지 항목을 닫을 때는 이 테이블에 한 줄만 더한다.
 */
const ROUTE_PERMISSION_TREE_GUARDS: Partial<
  Record<TeacherFeatureKey, (tree: RoutePermissionType) => boolean>
> = {
  ATTENDANCE: (tree) => tree.manage.attendance.access,
  TUITION: (tree) => tree.manage.tuition.access,
  MY_DB: (tree) => tree.prepare.학습지.나의DB.access,
  SETTLEMENT: (tree) => tree.myinfo.교재비정산.access,
}

export class UserPermission implements PermissionChecker {
  private readonly _routePermission: RoutePermission
  private readonly _subscriptionPermission: SubscriptionPermission
  private _featurePermission: FeaturePermission

  private _userAuthority: TeacherType
  private _academyFeatureAuthority: Academy['authority']

  constructor({
    userAuthority,
    academyFeatureAuthority,
    subscribedCurriculums,
    payStatus = '무료체험',
    isSchoolflat,
    isSeoltab,
    isFranchise,
    restrictions = [],
  }: {
    userAuthority: TeacherType
    academyFeatureAuthority: Academy['authority']
    subscribedCurriculums: Academy['subscribeItems']
    payStatus: Academy['payStatus']
    isSchoolflat: boolean
    isSeoltab: boolean
    isFranchise: boolean
    restrictions?: string[]
  }) {
    makeAutoObservable(this)
    this._userAuthority = userAuthority
    this._academyFeatureAuthority = academyFeatureAuthority

    this._routePermission = new RoutePermission(
      this._userAuthority,
      payStatus,
      isSchoolflat,
      isSeoltab,
      isFranchise,
    )
    this._subscriptionPermission = new SubscriptionPermission({
      academyFeatureAuthority: this._academyFeatureAuthority,
      payStatus,
      subscribeItems: subscribedCurriculums,
      isSeoltab,
    })
    this._featurePermission = new FeaturePermission(restrictions)
  }

  get get() {
    return this._routePermission.permissionByStatus
  }

  get bySubscription() {
    return this._subscriptionPermission
  }

  /** BE ALLOW/DENY 기반 기능 권한. 라우트 게이팅 및 in-page 렌더링 제어에 사용 */
  get byFeature(): FeaturePermission {
    return this._featurePermission
  }

  /** 권한 트리가 막는 feature key. 스쿨플랫·설탭처럼 트리로만 막힌 화면의 URL 직접 진입을 닫는다. */
  private get _routePermissionDeniedKeys(): TeacherFeatureKey[] {
    const tree = this.get

    return (
      Object.entries(ROUTE_PERMISSION_TREE_GUARDS) as [
        TeacherFeatureKey,
        (tree: RoutePermissionType) => boolean,
      ][]
    )
      .filter(([, isOpenedByTree]) => !isOpenedByTree(tree))
      .map(([key]) => key)
  }

  isRouteAllowed(pathname: string): boolean {
    return this._featurePermission.isRouteAllowed(pathname, this._routePermissionDeniedKeys)
  }

  updateRestrictions(restrictions: string[]): void {
    // 토큰 재발급 등으로 동일한 restrictions가 반복 주입될 때 새 인스턴스를 만들면
    // permission 참조가 매번 바뀌어, 이를 구독하는 앱 전역이 불필요하게 리렌더된다.
    if (this._featurePermission.hasSameRestrictions(restrictions)) return
    this._featurePermission = new FeaturePermission(restrictions)
  }

  get hasBasicAuth() {
    return this.checkPermission('BASIC')
  }

  get hasPremiumAuth() {
    return this.checkPermission('PREMIUM')
  }

  get hasSoloAuth() {
    return this.checkPermission('SOLO')
  }

  checkRentPermissionForSignatureWorkbook(feature?: CustomSignatureFeatureType) {
    if (!feature) return true

    if (feature !== 'RENT') return true

    if (this.bySubscription.workbook.signature.enableRentWorkbook) return true

    return false
  }

  checkPermission(target?: Academy['authority']): boolean {
    if (!target) return false
    return this._academyFeatureAuthority === target
  }
}
